import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PopupModal } from "../../components/Modal";
import { updateEmailMarketing } from "../../helpers/api/crm";
import type {
  EmailMarketingPmsType,
  EmailMarketingProfile,
  UpdateEmailMarketingDto,
} from "../../types/crm";

interface Props {
  customerId: string;
  profile: EmailMarketingProfile;
  onClose: () => void;
}

function LabeledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-content-tertiary">
        {label}
      </label>
      {children}
    </div>
  );
}

// The editable hotels-row text fields (latitude/longitude handled separately
// as numbers; pmsId is carried through silently from the snapshot).
const TEXT_FIELDS = [
  "name",
  "website",
  "email",
  "phone",
  "timezone",
  "defaultLocale",
  "addressLine1",
  "addressLine2",
  "city",
  "state",
  "postalCode",
  "countryCode",
  "currencyCode",
  "currencySymbol",
] as const;
type TextField = (typeof TEXT_FIELDS)[number];

const FIELD_LABELS: Record<TextField, string> = {
  name: "Hotel name",
  website: "Website",
  email: "Contact email",
  phone: "Phone",
  timezone: "Timezone (IANA)",
  defaultLocale: "Default locale",
  addressLine1: "Address line 1",
  addressLine2: "Address line 2",
  city: "City",
  state: "State / region",
  postalCode: "Postal code",
  countryCode: "Country code",
  currencyCode: "Currency code",
  currencySymbol: "Currency symbol",
};

/**
 * "Enable Email Marketing" modal. Replaces the bare toggle: the backend needs
 * a `hotels` row to enable the product, and for marketing-only customers that
 * row doesn't exist yet — so we collect/confirm the row fields here and INSERT
 * on submit (the backend UPDATEs instead when the row already exists).
 *
 * Cloudbeds/Mews marketing-only customers (PMS type set but no active user_pms)
 * also need PMS credentials, which the backend forwards to autumn-email's
 * pms_connections via the SSO JWT — never to user_pms. We surface those fields
 * only in that case (Cloudbeds: API key + property id; Mews: API key only).
 */
export function EnableEmailMarketingModal({
  customerId,
  profile,
  onClose,
}: Props) {
  const queryClient = useQueryClient();

  const [text, setText] = useState<Record<TextField, string>>(() => {
    const seed = {} as Record<TextField, string>;
    for (const f of TEXT_FIELDS) seed[f] = (profile.hotel[f] as string) ?? "";
    return seed;
  });
  const [latitude, setLatitude] = useState<string>(
    profile.hotel.latitude != null ? String(profile.hotel.latitude) : "",
  );
  const [longitude, setLongitude] = useState<string>(
    profile.hotel.longitude != null ? String(profile.hotel.longitude) : "",
  );

  // CB/Mews credentials are only relevant when the customer is on one of those
  // PMSs AND has no active user_pms row (a marketing-only customer). All-in-one
  // customers already reach autumn-email through the standard PMS route.
  const pmsType = (profile.pmsType ?? "").toLowerCase();
  const showPmsCreds =
    !profile.hasActivePms && (pmsType === "cloudbeds" || pmsType === "mews");
  const needsPropertyId = pmsType === "cloudbeds";
  const [pmsApiKey, setPmsApiKey] = useState("");
  const [pmsPropertyId, setPmsPropertyId] = useState(
    profile.hotel.pmsId ?? "",
  );

  const [validationError, setValidationError] = useState<string | null>(null);

  const setField = (f: TextField, v: string) =>
    setText((prev) => ({ ...prev, [f]: v }));

  const mutation = useMutation({
    mutationFn: (dto: UpdateEmailMarketingDto) =>
      updateEmailMarketing(customerId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["crm", "customer360", customerId],
      });
      queryClient.invalidateQueries({ queryKey: ["crm", "customers"] });
      onClose();
    },
  });

  const handleSave = () => {
    setValidationError(null);

    if (text.name.trim() === "") {
      setValidationError("Hotel name is required.");
      return;
    }

    const dto: UpdateEmailMarketingDto = { emailMarketingEnabled: true };
    for (const f of TEXT_FIELDS) {
      const v = text[f].trim();
      if (v !== "") dto[f] = v;
    }
    if (latitude.trim() !== "" && !isNaN(Number(latitude))) {
      dto.latitude = Number(latitude);
    }
    if (longitude.trim() !== "" && !isNaN(Number(longitude))) {
      dto.longitude = Number(longitude);
    }
    if (profile.hotel.pmsId) dto.pmsId = profile.hotel.pmsId;

    if (showPmsCreds) {
      if (pmsApiKey.trim() === "") {
        setValidationError(
          `An API key is required to connect ${needsPropertyId ? "Cloudbeds" : "Mews"}.`,
        );
        return;
      }
      if (needsPropertyId && pmsPropertyId.trim() === "") {
        setValidationError("A Cloudbeds property ID is required.");
        return;
      }
      dto.pmsType = pmsType as EmailMarketingPmsType;
      dto.pmsApiKey = pmsApiKey.trim();
      if (needsPropertyId) dto.pmsPropertyId = pmsPropertyId.trim();
    }

    mutation.mutate(dto);
  };

  const inputClass =
    "w-full px-3 py-2 text-sm rounded-md border border-border-secondary bg-surface-primary text-content-primary focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 placeholder:text-content-tertiary";

  const isCreate = !profile.hasHotelRow;

  // Render order for the two-column grid.
  const gridFields: TextField[] = useMemo(
    () => [
      "name",
      "website",
      "email",
      "phone",
      "addressLine1",
      "addressLine2",
      "city",
      "state",
      "postalCode",
      "countryCode",
      "timezone",
      "defaultLocale",
      "currencyCode",
      "currencySymbol",
    ],
    [],
  );

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="w-full max-w-2xl mx-auto max-h-[90dvh] overflow-y-auto"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-content-primary">
          Enable Email Marketing
        </h3>
        <button
          onClick={onClose}
          className="text-content-tertiary hover:text-content-primary transition-colors"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5"
          >
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <p className="text-[11px] text-content-tertiary -mt-2">
        {isCreate
          ? "This customer has no hotel record yet — confirm the details below to create it and turn on email marketing."
          : "Confirm the hotel details below, then enable email marketing."}
      </p>

      {(mutation.isError || validationError) && (
        <div className="px-4 py-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
          {validationError ?? "Failed to save changes. Please try again."}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {gridFields.map((f) => (
          <LabeledField key={f} label={FIELD_LABELS[f]}>
            <input
              value={text[f]}
              onChange={(e) => setField(f, e.target.value)}
              placeholder={
                f === "timezone"
                  ? "America/New_York"
                  : f === "countryCode"
                    ? "US"
                    : f === "currencyCode"
                      ? "USD"
                      : ""
              }
              className={inputClass}
            />
          </LabeledField>
        ))}
        <LabeledField label="Latitude">
          <input
            type="number"
            step="any"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            className={inputClass}
          />
        </LabeledField>
        <LabeledField label="Longitude">
          <input
            type="number"
            step="any"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
            className={inputClass}
          />
        </LabeledField>
      </div>

      {showPmsCreds && (
        <div className="flex flex-col gap-4 pt-2 border-t border-border-secondary">
          <div>
            <h4 className="text-sm font-semibold text-content-primary">
              {needsPropertyId ? "Cloudbeds" : "Mews"} connection
            </h4>
            <p className="text-[11px] text-content-tertiary mt-0.5">
              This customer has no PMS connection on file. These credentials are
              sent only to the email-marketing portal so it can read
              reservations — they are not added to the core PMS integration.
            </p>
          </div>
          <LabeledField
            label={needsPropertyId ? "Cloudbeds API key" : "Mews access token"}
          >
            <input
              value={pmsApiKey}
              onChange={(e) => setPmsApiKey(e.target.value)}
              placeholder={needsPropertyId ? "cbat_…" : "xxxxxxxx-xxxx-…"}
              className={inputClass}
              autoComplete="off"
            />
          </LabeledField>
          {needsPropertyId && (
            <LabeledField label="Cloudbeds property ID">
              <input
                value={pmsPropertyId}
                onChange={(e) => setPmsPropertyId(e.target.value)}
                className={inputClass}
                autoComplete="off"
              />
            </LabeledField>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-2">
        <button
          onClick={onClose}
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded-md border border-border-secondary hover:bg-surface-hover transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={mutation.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60"
        >
          {mutation.isPending
            ? "Enabling…"
            : isCreate
              ? "Create & enable"
              : "Enable"}
        </button>
      </div>
    </PopupModal>
  );
}
